import { Controller } from "@hotwired/stimulus"

// Right-side slide-over (import portfolio, paste). Opens over the current page,
// moves focus in, keeps focus inside while open, and restores it on close. The
// scrim and any element with data-action="drawer#close" dismiss it.
//
// The panel declares aria-modal="true", which promises the rest of the page is
// unreachable. Without a trap that promise is false: Tab walks straight out into
// the obscured page behind the scrim, and because Escape was bound to the panel
// it also stopped working once focus left. Both handlers are on the document
// while open, so they hold wherever focus is.
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",")

export default class extends Controller {
  static targets = ["panel", "scrim"]

  open(event) {
    event?.preventDefault()
    this.returnFocus = event?.currentTarget || document.activeElement
    this.panelTarget.classList.remove("hidden")
    this.scrimTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    this.panelTarget.setAttribute("aria-hidden", "false")
    this.panelTarget.focus()

    this.onKeydown = (e) => this.#handleKeydown(e)
    document.addEventListener("keydown", this.onKeydown)
  }

  close(event) {
    event?.preventDefault()
    this.#releaseKeydown()
    this.panelTarget.classList.add("hidden")
    this.scrimTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    this.panelTarget.setAttribute("aria-hidden", "true")
    // Return focus to the control that opened the drawer. It can be detached by
    // then (a Turbo refresh while the drawer was open), so guard before calling.
    if (this.returnFocus?.isConnected) this.returnFocus.focus()
  }

  // Kept for the panel's own data-action; the document listener does the work.
  closeOnEsc(event) {
    if (event.key === "Escape") this.close(event)
  }

  disconnect() {
    this.#releaseKeydown()
    document.body.classList.remove("overflow-hidden")
  }

  #handleKeydown(event) {
    if (this.panelTarget.classList.contains("hidden")) return

    if (event.key === "Escape") {
      this.close(event)
      return
    }
    if (event.key !== "Tab") return

    const focusable = [ ...this.panelTarget.querySelectorAll(FOCUSABLE) ]
      .filter((el) => el.offsetParent !== null || el === document.activeElement)
    if (focusable.length === 0) {
      // Nothing to land on — keep focus on the panel rather than letting it escape.
      event.preventDefault()
      this.panelTarget.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (event.shiftKey && (active === first || active === this.panelTarget)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    } else if (!this.panelTarget.contains(active)) {
      // Focus already escaped (or started outside) — pull it back in.
      event.preventDefault()
      first.focus()
    }
  }

  #releaseKeydown() {
    if (this.onKeydown) document.removeEventListener("keydown", this.onKeydown)
    this.onKeydown = null
  }
}
